// src/components/Admin/ProfitAndLossReportSection.js (Corrected)

import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import { Chart as ChartJS } from 'chart.js';

function ProfitAndLossReportSection() {
    const { allStations } = useAppContext();
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const reportRefs = useRef({});

    const [filterMonth, setFilterMonth] = useState(new Date().getMonth());
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterStation, setFilterStation] = useState('all');

    const [recipients, setRecipients] = useState([{ name: '', email: '' }]);
    const [isSavingEmails, setIsSavingEmails] = useState(false);

    // Fetch email settings on component load
    useEffect(() => {
        const fetchEmailSettings = async () => {
            try {
                const settings = await api.get('appConfig', { where: { id: 1 } });
                if (settings.length > 0 && settings[0].emailRecipients) {
                    setRecipients(settings[0].emailRecipients);
                }
            } catch (err) {
                console.warn("Could not fetch email settings.", err);
            }
        };
        fetchEmailSettings();
    }, []);

    const handleRecipientChange = (index, field, value) => {
        const newRecipients = [...recipients];
        newRecipients[index][field] = value;
        setRecipients(newRecipients);
    };

    const addRecipient = () => setRecipients([...recipients, { name: '', email: '' }]);
    
    const removeRecipient = (index) => {
        if (recipients.length > 1) {
            setRecipients(recipients.filter((_, i) => i !== index));
        }
    };

    const handleSaveEmails = async (e) => {
        e.preventDefault();
        setIsSavingEmails(true);
        try {
            await api.put('appConfig', 1, { emailRecipients: recipients });
            toast.success("Email settings saved successfully!");
        } catch (error) {
            toast.error("Failed to save email settings.");
        } finally {
            setIsSavingEmails(false);
        }
    };
    
    const handleGenerateReport = async (e) => {
        if(e) e.preventDefault();
        setIsLoading(true);
        setReportData(null);

        const startOfMonth = new Date(filterYear, filterMonth, 1).toISOString().slice(0, 10);
        const endOfMonth = new Date(filterYear, filterMonth + 1, 0).toISOString().slice(0, 10);

        try {
            const stationsToReportOn = filterStation === 'all' 
                ? allStations 
                : allStations.filter(s => s.id === filterStation);

            const stationReports = {};

            for (const station of stationsToReportOn) {
                const [incomeData, expensesData, pendingData] = await Promise.all([
                    api.get('ad_campaigns', { where: { stationId: station.id } }),
                    api.get('expenses', { where: { stationId: station.id } }),
                    api.get('invoices', { where: { stationId: station.id, status: ['Unpaid', 'Partially Paid'] } })
                ]);

                const filteredIncome = incomeData.filter(c => c.startDate >= startOfMonth && c.startDate <= endOfMonth);
                const filteredExpenses = expensesData.filter(e => e.date >= startOfMonth && e.date <= endOfMonth);

                const totalIncome = filteredIncome.reduce((sum, doc) => sum + (doc.totalCampaignAmount || 0), 0);
                const totalPending = pendingData.reduce((sum, doc) => sum + (doc.pendingAmount || 0), 0);
                
                const expensesByCategory = filteredExpenses.reduce((acc, expense) => {
                    const category = expense.category || 'Miscellaneous';
                    acc[category] = (acc[category] || 0) + (expense.amount || 0);
                    return acc;
                }, {});
                const totalExpenses = Object.values(expensesByCategory).reduce((sum, amount) => sum + amount, 0);

                if (totalIncome > 0 || totalExpenses > 0) {
                     stationReports[station.id] = {
                        stationName: station.name, totalIncome, totalExpenses, totalPending,
                        netProfit: totalIncome - totalExpenses, expensesByCategory,
                    };
                }
            }
            setReportData({
                period: `${new Date(startOfMonth).toLocaleDateString()} - ${new Date(endOfMonth).toLocaleDateString()}`,
                stations: stationReports
            });
        } catch (error) {
            console.error("Error generating report:", error);
            toast.error("Failed to generate report.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleExport = (stationId) => {
        const stationName = reportData.stations[stationId].stationName;
        toast.info(`Generating PDF for ${stationName}...`);
        const input = reportRefs.current[stationId];
        
        if (!input) { toast.error("Could not find report content to export."); return; }

        const button = input.querySelector('.no-print');
        if (button) button.style.display = 'none';

        // Temporarily disable chart animations for clean capture
        const originalAnimation = ChartJS.defaults.animation;
        ChartJS.defaults.animation = false;

        window.html2canvas(input, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
        .then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const { jsPDF } = window.jspdf;
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Profit_Loss_${stationName}_${filterYear}-${filterMonth+1}.pdf`);
            
            if (button) button.style.display = 'block';
            ChartJS.defaults.animation = originalAnimation; // Restore animation
        }).catch(() => {
            if (button) button.style.display = 'block';
            ChartJS.defaults.animation = originalAnimation; // Restore animation
            toast.error("Failed to generate PDF.");
        });
    };
    
    return (
        <div className="container mx-auto space-y-8">
            <div><h1 className="text-4xl font-bold text-gray-800">Monthly Profit & Loss Statement</h1><p className="text-gray-500 mt-1">Generate, preview, and export monthly financial summaries.</p></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-1 space-y-8">
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h3 className="font-bold text-lg text-gray-700 mb-4 border-b pb-2">Generate Report</h3>
                        <form onSubmit={handleGenerateReport} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div><label htmlFor="month-select" className="block text-sm font-medium">Month</label><select id="month-select" value={filterMonth} onChange={e => setFilterMonth(parseInt(e.target.value))} className="mt-1 block w-full bg-gray-50 p-3 rounded-md">{Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select></div>
                                <div><label htmlFor="year-select" className="block text-sm font-medium">Year</label><select id="year-select" value={filterYear} onChange={e => setFilterYear(parseInt(e.target.value))} className="mt-1 block w-full bg-gray-50 p-3 rounded-md">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select></div>
                            </div>
                            <div><label htmlFor="station-filter" className="block text-sm font-medium">Station</label><select id="station-filter" value={filterStation} onChange={e => setFilterStation(e.target.value)} className="mt-1 block w-full bg-gray-50 p-3 rounded-md"><option value="all">All Stations</option>{allStations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                            <button type="submit" disabled={isLoading} className="w-full bg-rose-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-rose-700 disabled:bg-gray-400">{isLoading ? "Generating..." : "Generate Preview"}</button>
                        </form>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <h3 className="font-bold text-lg text-gray-700 mb-4 border-b pb-2">Automated Email Settings</h3>
                         <form onSubmit={handleSaveEmails} className="space-y-4">
                            {recipients.map((recipient, index) => (<div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-2 border-b pb-3"><div><label className="block text-xs font-medium text-gray-600">Name</label><input type="text" value={recipient.name} onChange={e => handleRecipientChange(index, 'name', e.target.value)} placeholder="e.g., CEO" className="mt-1 block w-full bg-gray-50 p-2 rounded-md" required /></div><div className="relative"><label className="block text-xs font-medium text-gray-600">Email</label><input type="email" value={recipient.email} onChange={e => handleRecipientChange(index, 'email', e.target.value)} placeholder="name@example.com" className="mt-1 block w-full bg-gray-50 p-2 rounded-md" required />{index > 0 && <button type="button" onClick={() => removeRecipient(index)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs">X</button>}</div></div>))}
                            <button type="button" onClick={addRecipient} className="text-sm text-blue-600 hover:underline">+ Add another recipient</button>
                            <button type="submit" disabled={isSavingEmails} className="w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:bg-blue-700 disabled:bg-gray-400">{isSavingEmails ? "Saving..." : "Save Email Settings"}</button>
                        </form>
                    </div>
                </div>
                <div className="lg:col-span-2">
                    {reportData && reportData.stations ? (<div className="space-y-6">{Object.keys(reportData.stations).length > 0 ? Object.entries(reportData.stations).map(([stationId, data]) => (<div key={stationId} ref={el => reportRefs.current[stationId] = el} className="bg-white rounded-2xl shadow-lg p-6">
                        <div className="flex justify-between items-start mb-4 border-b pb-4">
                            <div><h3 className="text-2xl font-bold text-gray-800">{data.stationName}</h3><p className="text-gray-500">Financial Summary for period: {reportData.period}</p></div>
                            <button onClick={() => handleExport(stationId)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg no-print">Export PDF</button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center mb-6">
                            <div className="bg-blue-100 p-3 rounded-lg"><p className="text-xs font-semibold text-blue-700">Total Income</p><p className="text-xl font-bold text-blue-800">PKR {data.totalIncome.toLocaleString()}</p></div>
                            <div className="bg-orange-100 p-3 rounded-lg"><p className="text-xs font-semibold text-orange-700">Total Pending</p><p className="text-xl font-bold text-orange-800">PKR {data.totalPending.toLocaleString()}</p></div>
                            <div className="bg-red-100 p-3 rounded-lg"><p className="text-xs font-semibold text-red-700">Total Expenses</p><p className="text-xl font-bold text-red-800">PKR {data.totalExpenses.toLocaleString()}</p></div>
                            <div className={`${data.netProfit >= 0 ? 'bg-green-100' : 'bg-pink-100'} p-3 rounded-lg`}><p className={`text-xs font-semibold ${data.netProfit >= 0 ? 'text-green-700' : 'text-pink-700'}`}>Net Profit/Loss</p><p className={`text-xl font-bold ${data.netProfit >= 0 ? 'text-green-800' : 'text-pink-800'}`}>PKR {data.netProfit.toLocaleString()}</p></div>
                        </div>
                         <div className="bg-gray-50 p-4 rounded-xl border">
                             <h3 className="font-bold text-lg text-gray-700 mb-4">Expense Categories</h3>
                             <div className="space-y-2">{Object.keys(data.expensesByCategory).length > 0 ? Object.entries(data.expensesByCategory).map(([category, amount]) => (<div key={category} className="flex justify-between text-sm border-b pb-1"><span className="text-gray-600">{category}</span><span className="font-semibold">PKR {amount.toLocaleString()}</span></div>)) : <p className="text-sm text-gray-500">No expenses recorded for this station in this period.</p>}</div>
                         </div>
                    </div>)) : (<div className="bg-white rounded-2xl shadow-lg p-10 text-center"><p className="text-gray-500">No financial activity found for the selected filter.</p></div>)}</div>) 
                    : (<div className="bg-white rounded-2xl shadow-lg p-10 text-center h-full flex items-center justify-center"><p className="text-gray-500">{isLoading ? 'Generating report preview...' : 'Select filters and click "Generate Preview" to see the report.'}</p></div>)}
                </div>
            </div>
        </div>
    );
}

export default ProfitAndLossReportSection;